import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GraphQLError } from 'graphql';

import {
  IAddAccountInput,
  IAddOrbitalAccountInput,
  IMergePrepaidInput,
  IMergePrepaidPayload,
  IsoCountryCode,
  PaymentProcessor,
  UserAccountsDocument,
  useDeletePaymentMethodMutation,
  useMergePrepaidMutation,
  useUserAccountsQuery,
} from 'generated/graphql-gateway';
import { HttpErrorCodes } from 'remote/constants';
import {
  CustomEventNames,
  EventTypes,
  logEvent,
  logRBIEvent,
  updateAmplitudeUserAttributes,
} from 'state/amplitude';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { getConfigValue } from 'utils/environment';
import { toError } from 'utils/errors';
import { sanitizeAlphanumeric, sanitizeNumber } from 'utils/form';
import { ISOs } from 'utils/form/constants';
import logger from 'utils/logger';

import { CASH_ACCOUNT_IDENTIFIER } from '../constants';
import useApplePay from '../integrations/apple-pay/use-apple-pay';
import { getNonce } from '../integrations/first-data/client';
import { storePrepaidCard } from '../integrations/first-data/store-prepaid-card';
import { useCanUseGooglePay } from '../integrations/google-pay/use-can-use-google-pay';
import { useGooglePay } from '../integrations/google-pay/use-google-pay';
import { IEncryptionResult } from '../integrations/orbital';
import {
  CurrentSelectedMethodType,
  GetPaymentMethodsErrorBehavior,
  IAddPaymentMethodOptions,
  IGetPaymentMethodsOptions,
  IPaymentMethod,
  IPaymentPayload,
  IPaymentState,
  IReloadPrepaidCard,
} from '../types';
import { getPaymentMethodId, splitExpiry } from '../utils';

import { getPaymentMethodsState } from './getPaymentMethodsState';
import {
  filterExpiredCreditCards,
  transformPaymentDetailsToPaymentMethod,
} from './getPaymentMethodsState/utils';
import { usePrepaidsReload } from './prepaid/use-prepaids-reload';
import { IInitPaymentMethods, IPayment } from './types';

const cashAccount: IPaymentMethod = {
  cash: true,
  fdAccountId: CASH_ACCOUNT_IDENTIFIER,
  accountIdentifier: CASH_ACCOUNT_IDENTIFIER,
  orbitalIdentifier: null,
  credit: null,
  prepaid: null,
  paypal: null,
  venmo: null,
  cashapp: null,
};

type PaymentMethodRemovalSource = 'explicit-delete' | 'replacement';
type VaultPaymentMethodOperation = 'add' | 'replace';

/** @deprecated do not use this hook directly it is a singleton. use usePaymentContext/PaymentProvider instead */
export const usePayment = ({
  getEncryptionDetailsMutation,
  openErrorDialog,
  user,
  isReAuthenticating,
  updateUserInfo,
  addCreditAccountMutation,
  paymentDetails,
  isQuickPayMethodSelected,
}: IPayment) => {
  const { feCountryCode } = useLocale();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [hasGetPaymentMethodsError, setHasGetPaymentMethodsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { applePayCardDetails, canUseApplePay } = useApplePay({});
  const { canUseGooglePay } = useCanUseGooglePay();
  const { googlePayCardDetails } = useGooglePay();
  const enableCashPayment = useFlag(LaunchDarklyFlag.ENABLE_CASH_PAYMENT);
  const enableCashApp = useFlag(LaunchDarklyFlag.ENABLE_CASH_APP_PAYMENTS);
  const enablePayPal = useFlag(LaunchDarklyFlag.ENABLE_PAYPAL_PAYMENTS);
  const enableVenmo = useFlag(LaunchDarklyFlag.ENABLE_VENMO_PAYMENTS);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const [fireDeleteAccountMutation] = useDeletePaymentMethodMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });
  const [fireMergePrepaidMutation] = useMergePrepaidMutation({
    refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }],
  });

  const fdAccessTokenFirstRequest = useRef<string | null>(null);

  const { braintreeAuthorizationKey, baseUrl: fdUrl } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const {
    data: userAccountsData,
    loading: accountsLoading,
    refetch: refetchPaymentMethods,
  } = useUserAccountsQuery({
    variables: { feCountryCode: IsoCountryCode[feCountryCode] },
    // Apollo client loading state gets stuck: https://github.com/apollographql/react-apollo/issues/3425
    // Temporary fix while we wait for a stable 3.0.0 version
    fetchPolicy: 'cache-and-network',
    skip: !user,
  });

  const { cognitoId, details } = user || {};
  const { defaultPaymentAccountId: defaultPrepaidPaymentMethodId } = details || {};

  // note: defaultFdAccountId is deprecated. use defaultAccountIdentifier if present
  const defaultAccountIdentifier = details?.defaultAccountIdentifier || details?.defaultFdAccountId;

  const [checkoutPaymentMethodId, setCheckoutPaymentMethodId] = useState('');
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [defaultReloadPaymentMethodId, setDefaultReloadPaymentMethodId] = useState('');
  const [prepaidReloadPaymentMethodId, setPrepaidReloadPaymentMethodId] = useState('');
  const [paymentMethodHasBeenInit, setPaymentMethodHasBeenInit] = useState(false);
  const [savingReloadPaymentMethod, setSavingReloadPaymentMethod] = useState(false);
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);
  const checkoutPaymentMethodIdRef = useRef(checkoutPaymentMethodId);
  // A failed vault leaves the update route open, so its retry must resume after confirmed removal.
  const completedReplacementRemovalsRef = useRef(new Set<string>());

  useEffect(() => {
    checkoutPaymentMethodIdRef.current = checkoutPaymentMethodId;
  }, [checkoutPaymentMethodId]);

  const saveNewPaymentMethodEvent = useCallback(
    (success: boolean, message?: string, context: Record<string, any> = {}) => {
      logEvent(CustomEventNames.SAVE_NEW_PAYMENT_METHOD, EventTypes.Other, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
        'Payment Processor': context.paymentProcessor,
      });
      if (success) {
        logger.info({
          message: 'Payment method added',
          context,
        });
      } else {
        logger.error({
          message: 'Error adding payment method',
          context,
        });
      }
    },
    []
  );

  const deletePaymentMethodEvent = useCallback((success: boolean, message?: string) => {
    logEvent(CustomEventNames.DELETE_PAYMENT_METHOD, EventTypes.Other, {
      Response: success ? 'Successful' : 'Failure',
      'Response Description': success ? 'Successful' : message,
    });
  }, []);

  const logUserPaymentIdentity = useCallback(
    (accountIdentifier: string) => {
      if (!user || !details) {
        return;
      }
      const selectedMethod = paymentMethods.find(
        method =>
          method.accountIdentifier === accountIdentifier || method.fdAccountId === accountIdentifier
      );
      if (!selectedMethod) {
        return;
      }
      let ccToken;
      if (selectedMethod.credit) {
        ccToken = selectedMethod.credit.panToken;
      } else if (selectedMethod.prepaid) {
        ccToken = selectedMethod.prepaid.cardNumber;
      }

      if (ccToken) {
        updateAmplitudeUserAttributes({
          ccToken,
        });
      }
    },
    [details, paymentMethods, user]
  );

  const setAndLogCheckoutPaymentMethodId = useCallback(
    (fdAccountId: string) => {
      logUserPaymentIdentity(fdAccountId);
      setCheckoutPaymentMethodId(fdAccountId);
    },
    [logUserPaymentIdentity, setCheckoutPaymentMethodId]
  );

  const setAndLogDefaultReloadPaymentMethodId = useCallback(
    async (accountId: string) => {
      setSavingReloadPaymentMethod(true);
      setPrepaidReloadPaymentMethodId(accountId);
      setDefaultReloadPaymentMethodId(accountId);
      try {
        if (user) {
          await updateUserInfo(
            { defaultReloadPaymentMethodId: accountId },
            { skipIdentityUpdates: true }
          );
        }
      } catch (error) {
        logger.error({
          error,
          message: 'Error saving default reload payment method preference',
          context: { accountId },
        });
      } finally {
        setSavingReloadPaymentMethod(false);
      }
    },
    [user, updateUserInfo]
  );

  const updatePaymentMethodsState = useCallback(
    (
      validDefaultPaymentMethodId: string,
      validDefaultReloadPaymentMethodId: string,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      if (
        checkoutPaymentMethodIdRef.current === validDefaultPaymentMethodId ||
        isQuickPayMethodSelected
      ) {
        return;
      }
      /*
      call updateMe query only if user has a invalid defaultAccountIdentifier
      or if it's different than the validDefaultPaymentMethodId returned by the getPaymentMethodsState hook
      */
      const shouldUpdateUser =
        (!user?.details.defaultAccountIdentifier && !!validDefaultPaymentMethodId) ||
        validDefaultPaymentMethodId !== (user?.details.defaultAccountIdentifier ?? '');

      setDefaultPaymentMethodId(validDefaultPaymentMethodId);
      setCheckoutPaymentMethodId(validDefaultPaymentMethodId);
      setPrepaidReloadPaymentMethodId(validDefaultReloadPaymentMethodId);
      setDefaultReloadPaymentMethodId(validDefaultReloadPaymentMethodId);

      if (user && shouldUpdateUser && !isQuickPayMethodSelected) {
        // Update the checkout and reload payment method when adding a new CC
        updateUserInfo(
          {
            defaultCheckoutPaymentMethodId: validDefaultPaymentMethodId,
            defaultReloadPaymentMethodId: validDefaultReloadPaymentMethodId,
          },
          {
            shouldMuteUserInfoErrors: shouldMuteUpdateUserInfoErrors,
            skipIdentityUpdates: true,
          }
        );
      }
    },
    [updateUserInfo, user, isQuickPayMethodSelected]
  );

  const setAndLogDefaultPaymentMethodId = useCallback(
    async (accountId: string, isAddingNewCc?: boolean) => {
      setSavingPaymentMethod(true);
      logUserPaymentIdentity(accountId);
      setDefaultPaymentMethodId(accountId);
      // Set the checkout payment method when the user change its default one
      setCheckoutPaymentMethodId(accountId);

      try {
        if (user && !isQuickPayMethodSelected) {
          // Update the checkout and reload payment method when adding a new CC
          await updateUserInfo(
            {
              defaultCheckoutPaymentMethodId: accountId,
              ...(isAddingNewCc ? { defaultReloadPaymentMethodId: accountId } : {}),
            },
            { skipIdentityUpdates: true }
          );
        }
      } catch (error) {
        logger.error({
          error,
          message: 'Error saving default checkout payment method preference',
          context: { accountId, isAddingNewCc },
        });
      } finally {
        setSavingPaymentMethod(false);
      }
    },
    [logUserPaymentIdentity, user, updateUserInfo, isQuickPayMethodSelected]
  );

  const getEncryptionDetails = useCallback(async () => {
    const encryptionDetailsResponse = await getEncryptionDetailsMutation();

    if (!encryptionDetailsResponse.data) {
      throw new Error('Missing encryption details');
    }

    return encryptionDetailsResponse.data.encryptionDetails;
  }, [getEncryptionDetailsMutation]);

  const clearPaymentMethods = useCallback(() => {
    setPaymentMethods(old => {
      // if old 0 new 0 no need to cause re-renders
      if (old?.length === 0) {
        return old;
      }
      return [];
    });
    setHasGetPaymentMethodsError(false);
    setLoading(false);
  }, []);

  const getPaymentMethods = useCallback(
    async (options: IGetPaymentMethodsOptions) => {
      try {
        if (cognitoId) {
          await refetchPaymentMethods();
        }
      } catch (error) {
        const errorBehavior: GetPaymentMethodsErrorBehavior =
          options?.errorBehavior ?? 'show-dialog';
        logger.error({
          error,
          message: 'Error getting payment methods',
          context: {
            errorBehavior,
            source: options.source,
          },
        });
        if (errorBehavior === 'log-only') {
          return;
        }
        clearPaymentMethods();
        setHasGetPaymentMethodsError(true);
        openErrorDialog({
          message: t('paymentLoadingError'),
          modalAppearanceEventMessage: 'Error: Fetching Payment Methods Failure',
          error: toError(error),
        });
      }
    },
    [refetchPaymentMethods, clearPaymentMethods, openErrorDialog, cognitoId]
  );

  const oneTimePaymentMethod: IPaymentMethod | undefined = useMemo(() => {
    return transformPaymentDetailsToPaymentMethod(paymentDetails);
  }, [paymentDetails]);

  /**
   * set up a bunch of state related to a customer's payment accounts and methods
   * @param {IPaymentMethod[]} accounts - list of ways a customer could pay (prepaid, credit card, mobile wallets etc)
   * @param {string} paymentMethodId - attempt to use this as the default payment method
   * @param {string} prepaidPaymentMethodId - attempt to use this as the default prepaid payment method
   */
  const initPaymentMethods = useCallback(
    (
      { accounts, paymentMethodId, prepaidPaymentMethodId }: IInitPaymentMethods,
      shouldMuteUpdateUserInfoErrors?: boolean
    ) => {
      const accountsWithOneTimePaymentMethod = [
        ...(oneTimePaymentMethod ? [oneTimePaymentMethod] : []),
        ...accounts,
      ];
      // Get valid payment methods
      const {
        availablePaymentMethodList,
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
      } = getPaymentMethodsState({
        paymentMethodId,
        prepaidPaymentMethodId,
        accounts: accountsWithOneTimePaymentMethod,
        canUseApplePay,
        enableCashPayment,
        enableCashApp,
        enablePayPal,
        enableVenmo,
        applePayCardDetails,
        canUseGooglePay,
        googlePayCardDetails,
        paymentMethodHasBeenInit,
        braintreeAuthorizationKey,
      });

      // Setup the payment methods into a valid state
      setPaymentMethods(old => {
        // if old 0 new 0 no need to cause re-renders
        if (old?.length === 0 && availablePaymentMethodList?.length === 0) {
          return old;
        }

        return availablePaymentMethodList;
      });

      // skip update payment methods when selected method is one time payment
      if (paymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD) {
        return;
      }

      updatePaymentMethodsState(
        validDefaultPaymentMethodId,
        validDefaultReloadPaymentMethodId,
        shouldMuteUpdateUserInfoErrors
      );
    },
    [
      applePayCardDetails,
      braintreeAuthorizationKey,
      canUseGooglePay,
      oneTimePaymentMethod,
      canUseApplePay,
      enableCashPayment,
      enableCashApp,
      enablePayPal,
      enableVenmo,
      googlePayCardDetails,
      paymentMethodHasBeenInit,
      updatePaymentMethodsState,
    ]
  );

  useEffect(() => {
    // To avoid a race condition detected with the updateUserInfo while the user is re-authenticating, payment method initialization is skipped.
    // Additionally, we need to prevent the clearing of payment methods since we are not initializing them during user re-authentication.
    if (!user && !isReAuthenticating) {
      clearPaymentMethods();
      return;
    }
    if (userAccountsData) {
      const accounts: IPaymentMethod[] =
        (userAccountsData.userAccounts &&
          userAccountsData.userAccounts.accounts &&
          userAccountsData.userAccounts.accounts.filter<IPaymentMethod>(
            (account): account is IPaymentMethod =>
              !!account && (!!account.fdAccountId || !!account.accountIdentifier)
          )) ||
        [];

      setHasGetPaymentMethodsError(false);

      if (accounts.length) {
        storePrepaidCard({ accounts });
      }
    }
    setLoading(false);
  }, [clearPaymentMethods, user, userAccountsData, isReAuthenticating]);

  const { reloadPrepaidsCardMutation, loading: prepaidLoading } = usePrepaidsReload({
    openErrorDialog,
  });

  const getPrepaidPaymentMethod = useCallback(() => {
    return paymentMethods.find(method => Boolean(method.prepaid)) || null;
  }, [paymentMethods]);

  const getBalanceFromPaymentMethods = useCallback((prepaidPaymentMethod: IPaymentMethod) => {
    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
        return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
      } else if (prepaidPaymentMethod.prepaid.currentBalance) {
        return prepaidPaymentMethod.prepaid.currentBalance;
      }
    }
    return 0;
  }, []);

  const getPrepaidCardNumber = useCallback(() => {
    const prepaidPaymentMethod = getPrepaidPaymentMethod();

    if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
      // add spaces between every 4 characters in the card number
      return (prepaidPaymentMethod.prepaid.cardNumber.match(/.{1,4}/g) || []).join(' ');
    }
    return null;
  }, [getPrepaidPaymentMethod]);

  const removePaymentMethod = useCallback(
    async (
      accountIdentifier: string,
      { showErrorDialog, source }: { showErrorDialog: boolean; source: PaymentMethodRemovalSource }
    ): Promise<IPaymentMethod[]> => {
      try {
        setIsDeleting(true);
        const { data } = await fireDeleteAccountMutation({
          variables: { input: { storedPaymentMethodId: accountIdentifier } },
        });

        if (data?.deletePaymentMethod.success !== true) {
          throw new Error('Payment method deletion was not confirmed');
        }

        const updatedPaymentMethods = paymentMethods.filter(
          method => getPaymentMethodId(method) !== accountIdentifier
        );
        const paymentMethodIdAfterDelete =
          defaultAccountIdentifier !== accountIdentifier
            ? defaultAccountIdentifier
            : updatedPaymentMethods[0]
            ? getPaymentMethodId(updatedPaymentMethods[0])
            : '';

        initPaymentMethods({
          accounts: updatedPaymentMethods,
          paymentMethodId: paymentMethodIdAfterDelete || '',
          prepaidPaymentMethodId: paymentMethodIdAfterDelete || '',
        });

        deletePaymentMethodEvent(true);
        return updatedPaymentMethods;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error({
          error,
          message: 'Error deleting payment method',
          context: { source },
        });

        deletePaymentMethodEvent(false, errorMessage);
        if (showErrorDialog) {
          openErrorDialog({
            message: t('paymentDeletingError'),
            modalAppearanceEventMessage: 'Error: Deleting Payment Method Failure',
            error: toError(error),
          });
        }
        throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    [
      fireDeleteAccountMutation,
      paymentMethods,
      defaultAccountIdentifier,
      initPaymentMethods,
      deletePaymentMethodEvent,
      openErrorDialog,
    ]
  );

  const deletePaymentMethod = useCallback(
    async (accountIdentifier: string) => {
      try {
        await removePaymentMethod(accountIdentifier, {
          showErrorDialog: true,
          source: 'explicit-delete',
        });
      } catch {
        // The explicit delete surface owns its error dialog through removePaymentMethod.
      }
    },
    [removePaymentMethod]
  );

  const paymentProcessor = userAccountsData?.userAccounts?.paymentProcessor;
  const isFirstData = paymentProcessor === PaymentProcessor.FIRSTDATA;
  const isOrbital = paymentProcessor === PaymentProcessor.ORBITAL;

  // Expected value that is applied to hidden form fields for payment method
  const hiddenFieldValue = null;

  const transformPaymentValues = useCallback(
    ({
      paymentValues,
      paymentFieldVariations = defaultPaymentFieldVariation,
    }: {
      paymentValues: IPaymentState;
      paymentFieldVariations?: PaymentFieldVariations;
    }): IPaymentPayload => {
      let expiryDate: { month: string; year: string } | null = null;
      const cardNumber = sanitizeNumber(paymentValues.cardNumber);
      const { expiryMonth, expiryYear } = splitExpiry(paymentValues.expiry ?? '');
      expiryDate = paymentValues.expiry
        ? {
            month: expiryMonth,
            year: expiryYear,
          }
        : null;

      const country = paymentValues.billingCountry ? ISOs[paymentValues.billingCountry] : null;
      const postalCode = sanitizeAlphanumeric(paymentValues.billingZip);
      return {
        billingAddress: {
          country: paymentFieldVariations.country ? country : hiddenFieldValue,
          postalCode: paymentFieldVariations.zip ? postalCode : '',
          unitNumber: paymentFieldVariations.addressLine2
            ? paymentValues.billingApt
            : hiddenFieldValue,
          locality: paymentFieldVariations.city ? paymentValues.billingCity : hiddenFieldValue,
          region: paymentFieldVariations.state ? paymentValues.billingState : hiddenFieldValue,
          streetAddress: paymentFieldVariations.addressLine1
            ? paymentValues.billingStreetAddress
            : hiddenFieldValue,
        },
        cardNumber,
        cardType: paymentValues.cardType ?? '',
        expiryDate,
        nameOnCard: paymentValues.nameOnCard,
        securityCode: paymentValues.cvv,
      };
    },
    [hiddenFieldValue]
  );

  const [encryptionResult, setEncryptionResult] = useState<IEncryptionResult | undefined>();

  const vaultPaymentMethod = useCallback(
    async (
      paymentValues: IPaymentPayload,
      { skipErrorDialogOnError = false, isReloadMethod = false }: IAddPaymentMethodOptions = {},
      encryptionResult?: IEncryptionResult,
      existingPaymentMethods: IPaymentMethod[] = paymentMethods,
      operation: VaultPaymentMethodOperation = 'add'
    ) => {
      const { billingAddress, cardNumber, nameOnCard: fullName } = paymentValues;

      if (!loading) {
        setLoading(true);
      }

      try {
        let addAccount;
        let accountIdentifier;

        // Handles adding a payment method when the payment processor is Orbital
        if (isOrbital) {
          if (!encryptionResult) {
            throw new Error(
              'Orbital encryption results encryption results not available for add payment method'
            );
          }
          const orbitalEncryptionResult = encryptionResult;

          if (!orbitalEncryptionResult || !('cryptCard' in orbitalEncryptionResult)) {
            throw new Error('Orbital Encryption Failure');
          }

          const orbitalInput: IAddOrbitalAccountInput = {
            encryptedCardNum: orbitalEncryptionResult.cryptCard,
            encryptedCvv: orbitalEncryptionResult.cryptCvv,
            expiryMonth: paymentValues.expiryDate?.month ?? '',
            expiryYear: paymentValues.expiryDate?.year ?? '',
            pieFormat: orbitalEncryptionResult.pieFormat,
            pieMode: orbitalEncryptionResult.mode,
            piePhaseId: String(orbitalEncryptionResult.phase),
            pieIntegrityCheck: orbitalEncryptionResult.integrityCheck,
            pieSubscriberId: orbitalEncryptionResult.subscriberId,
            pieKeyID: orbitalEncryptionResult.keyId,
            cardBrand: paymentValues.cardType.toLowerCase(),
            bin: cardNumber.substring(0, 6),
          };
          const addAccountResponse = await addCreditAccountMutation({
            variables: {
              input: {
                billingAddress,
                orbitalInput,
                fullName: paymentValues.nameOnCard,
              },
            },
          });

          // Assertion of minimum data for add account.
          if (!addAccountResponse.data?.addCreditAccount.accountIdentifier) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? '';
        } else {
          // Handles adding a payment method when the payment processor is First Data
          const ccBin = cardNumber.slice(0, 6);

          let firstDataInput: IAddAccountInput = { ccBin: '', fdAccessToken: '', fdNonce: '' };
          let addAccountResponse: any = {};

          const {
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            fdCustomerId, // Necessary second request
            algorithm,
          } = await getEncryptionDetails();

          if (!fdAccessTokenFirstRequest.current) {
            fdAccessTokenFirstRequest.current = fdAccessToken;
          }

          const nonceResponse = await getNonce(
            paymentValues,
            fdPublicKey,
            fdApiKey,
            fdAccessToken,
            fdCustomerId ?? null,
            fdUrl,
            onlySendPostalCode,
            algorithm ?? undefined
          );
          const { token } = await nonceResponse.json();
          const fdNonce = token.tokenId;

          firstDataInput = {
            fdAccessToken,
            fdNonce,
            ccBin,
          };

          try {
            addAccountResponse = await addCreditAccountMutation({
              variables: {
                input: {
                  billingAddress,
                  firstDataInput,
                  fullName,
                },
              },
            });
          } catch (error) {
            throw error;
          }

          // Assertion of minimun data for add account.
          if (
            !addAccountResponse.data?.addCreditAccount.accountIdentifier &&
            !addAccountResponse.data?.addCreditAccount.fdAccountId
          ) {
            throw new Error('Add Account Failure');
          }

          addAccount = addAccountResponse.data.addCreditAccount;
          accountIdentifier = addAccount.accountIdentifier ?? addAccount.fdAccountId ?? '';
        }

        saveNewPaymentMethodEvent(true, undefined, {
          accountIdentifier,
          operation,
          paymentProcessor,
        });

        logRBIEvent(
          {
            name: CustomEventNames.NEW_PAYMENT_METHOD_SAVED,
            type: EventTypes.Other,
            attributes: {
              paymentMethod: 'CreditCard',
              cardBrand: addAccount?.credit?.cardType,
            },
          },
          { skipLoggingToBraze: true }
        );

        const isReloadPaymentMethod = isReloadMethod;

        // When a user adds a CC, set the Checkout and Reload Default payment method to the newly added one
        initPaymentMethods({
          accounts: [...existingPaymentMethods, addAccount as IPaymentMethod],
          paymentMethodId: isReloadPaymentMethod ? checkoutPaymentMethodId : accountIdentifier,
          prepaidPaymentMethodId: isReloadPaymentMethod
            ? checkoutPaymentMethodId
            : accountIdentifier,
        });

        if (isReloadPaymentMethod) {
          await setAndLogDefaultReloadPaymentMethodId(accountIdentifier);
        } else {
          await setAndLogDefaultPaymentMethodId(accountIdentifier, true);
        }

        // The vault mutation has committed at this point. Refresh failures are log-only so they
        // cannot turn a successful add into a retry that submits the same card again.
        await getPaymentMethods({
          errorBehavior: 'log-only',
          source: 'add-payment-method-success',
        });

        return accountIdentifier;
      } catch (error) {
        logger.error({
          error,
          message: 'Error adding payment method',
          context: { operation },
        });
        const err = toError(error);
        saveNewPaymentMethodEvent(false, err.message, {
          accountIdentifier: undefined,
          message: err.message,
          operation,
          paymentProcessor,
        });
        if (!skipErrorDialogOnError) {
          const rawStatusCode = (error as {
            graphQLErrors?: GraphQLError[];
          }).graphQLErrors?.find((graphQLError: GraphQLError) =>
            Number.isInteger(graphQLError?.extensions?.statusCode)
          )?.extensions?.statusCode;
          const statusCode: number | undefined =
            typeof rawStatusCode === 'number' ? rawStatusCode : undefined;
          openErrorDialog({
            message:
              statusCode === HttpErrorCodes.TooManyRequests
                ? t('tooManyAddAccountAttempts')
                : t('paymentAddingError'),
            modalAppearanceEventMessage: 'Error: Adding Payment Method Failure',
            error: err,
          });
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      addCreditAccountMutation,
      checkoutPaymentMethodId,
      fdUrl,
      getEncryptionDetails,
      getPaymentMethods,
      initPaymentMethods,
      isOrbital,
      loading,
      onlySendPostalCode,
      openErrorDialog,
      paymentMethods,
      paymentProcessor,
      saveNewPaymentMethodEvent,
      setAndLogDefaultPaymentMethodId,
      setAndLogDefaultReloadPaymentMethodId,
    ]
  );

  const addPaymentMethod = useCallback(
    (
      paymentValues: IPaymentPayload,
      options?: IAddPaymentMethodOptions,
      encryptionResult?: IEncryptionResult
    ) => vaultPaymentMethod(paymentValues, options, encryptionResult),
    [vaultPaymentMethod]
  );

  const replacePaymentMethod = useCallback(
    async (
      accountIdentifier: string,
      paymentValues: IPaymentPayload,
      options?: IAddPaymentMethodOptions,
      encryptionResult?: IEncryptionResult
    ) => {
      let remainingPaymentMethods = paymentMethods.filter(
        method => getPaymentMethodId(method) !== accountIdentifier
      );

      if (!completedReplacementRemovalsRef.current.has(accountIdentifier)) {
        remainingPaymentMethods = await removePaymentMethod(accountIdentifier, {
          showErrorDialog: false,
          source: 'replacement',
        });
        completedReplacementRemovalsRef.current.add(accountIdentifier);
      }

      const replacementIdentifier = await vaultPaymentMethod(
        paymentValues,
        options,
        encryptionResult,
        remainingPaymentMethods,
        'replace'
      );
      completedReplacementRemovalsRef.current.delete(accountIdentifier);

      return replacementIdentifier;
    },
    [paymentMethods, removePaymentMethod, vaultPaymentMethod]
  );

  const reloadPrepaidCard = useCallback(
    async (reloadInfo: IReloadPrepaidCard) => {
      try {
        const { currencyCode, cardType, fundValue } = reloadInfo;

        const { data } = await reloadPrepaidsCardMutation(reloadInfo);

        logEvent(CustomEventNames.BUTTON_CLICK_TIMS_CARD_RELOAD, EventTypes.Transaction, {
          Currency: currencyCode,
          'Card Type': cardType,
          'Reload Amount': fundValue / 100,
        });

        return data?.prepaidsReload?.currentBalance;
      } catch (error) {
        logger.error({
          error,
          message: 'Error reloading prepaid card',
        });
        return undefined;
      }
    },
    [reloadPrepaidsCardMutation]
  );

  const mergePrepaidCardBalances = useCallback(
    async ({ sourceCardNumber, sourcePin }: IMergePrepaidInput): Promise<IMergePrepaidPayload> => {
      if (!loading) {
        setLoading(true);
      }

      try {
        const { data: wrapper } = await fireMergePrepaidMutation({
          variables: {
            input: {
              sourceCardNumber: sourceCardNumber.replace(/\s/g, ''),
              sourcePin,
            },
          },
        });

        const data = wrapper!.mergePrepaid;

        await getPaymentMethods({ source: 'merge-prepaid-card-balances' });

        // Set the newly added GC, or the desitination GC as the checkout payment method.
        setCheckoutPaymentMethodId(data.storedPaymentMethodId || '');

        return data;
      } finally {
        setLoading(false);
      }
    },
    [fireMergePrepaidMutation, getPaymentMethods, loading]
  );

  const checkoutPaymentMethod = paymentMethods.find(
    ({ accountIdentifier, fdAccountId }) =>
      accountIdentifier === checkoutPaymentMethodId || fdAccountId === checkoutPaymentMethodId
  );

  const userAccounts = userAccountsData?.userAccounts?.accounts;

  useEffect(() => {
    if (!userAccounts || isReAuthenticating) {
      return;
    }

    const accountsMaybeWithCash = enableCashPayment ? [...userAccounts, cashAccount] : userAccounts;
    const selectedCheckoutPaymentMethodId = checkoutPaymentMethodIdRef.current;
    const paymentMethodId =
      selectedCheckoutPaymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD
        ? selectedCheckoutPaymentMethodId
        : defaultAccountIdentifier || '';

    setPaymentMethodHasBeenInit(true);
    initPaymentMethods(
      {
        accounts: accountsMaybeWithCash as IPaymentMethod[],
        paymentMethodId,
        prepaidPaymentMethodId: defaultPrepaidPaymentMethodId || '',
      },
      true
    );
  }, [
    canUseApplePay,
    canUseGooglePay,
    cognitoId,
    defaultAccountIdentifier,
    defaultPrepaidPaymentMethodId,
    enableCashApp,
    enableCashPayment,
    enablePayPal,
    enableVenmo,
    initPaymentMethods,
    isReAuthenticating,
    oneTimePaymentMethod,
    userAccounts,
  ]);

  // This state ensures that no payment methods are available after loading the user's account information.
  // Native wallets (Apple Pay / Google Pay) count as available methods even when the user has no saved cards.
  const noPaymentMethodsAvailable = useMemo(
    () =>
      !!userAccountsData?.userAccounts &&
      !filterExpiredCreditCards(paymentMethods).length &&
      !canUseApplePay &&
      !canUseGooglePay,
    [paymentMethods, userAccountsData?.userAccounts, canUseApplePay, canUseGooglePay]
  );

  const savingPaymentMethods = useMemo(() => savingPaymentMethod || savingReloadPaymentMethod, [
    savingPaymentMethod,
    savingReloadPaymentMethod,
  ]);

  return {
    loading: loading || accountsLoading || prepaidLoading || isDeleting,
    // savingPaymentMethods state is not included in the 'loading' state to prevent the
    // payment methods page from reloading every time a default payment method is selected.
    savingPaymentMethods,
    noPaymentMethodsAvailable,
    paymentMethods,
    getPaymentMethods,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    getPrepaidCardNumber,
    getEncryptionDetails,
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId: setAndLogCheckoutPaymentMethodId,
    defaultPaymentMethodId,
    defaultReloadPaymentMethodId,
    encryptionResult,
    setEncryptionResult,
    setDefaultPaymentMethodId: setAndLogDefaultPaymentMethodId,
    setDefaultReloadPaymentMethodId: setAndLogDefaultReloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    prepaidReloadPaymentMethodId,
    addPaymentMethod,
    replacePaymentMethod,
    deletePaymentMethod,
    reloadPrepaidCard,
    mergePrepaidCardBalances,
    hasGetPaymentMethodsError,
    canUseApplePay,
    canUseGooglePay,
    paymentProcessor,
    isFirstData,
    isOrbital,
    transformPaymentValues,
    setLoading,
  };
};

/** @deprecated use usePayment instead. */
export const _usePayment = usePayment;
